import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Wind, Sparkles, Play, ShieldAlert } from "lucide-react";
import { useStore } from "../context/StoreContext";
import BreathBreak from "./BreathBreak";
import YogaBreak from "./YogaBreak";
import { SOFT_BREAK_AT, SNOOZE_SECONDS, HARD_CAP, ZEN_REWARD, YOGA_REWARD } from "../data/playtime";
import sfx from "../utils/sound";
import { trackEvent } from "../utils/analytics";

const TICK_SECS = 5;
const fmtMin = (s) => Math.floor(s / 60);

const PlaytimeTracker = () => {
  const {
    playSeconds,
    playSnoozeAt,
    playCapped,
    incrementPlay,
    resetPlaySession,
    setPlaySnooze,
    setPlayCapped,
    addCoins,
    pushNotification,
  } = useStore();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const onGames = pathname.startsWith("/games");
  const [zen, setZen] = useState(false);
  const [yoga, setYoga] = useState(false);
  const [nudge, setNudge] = useState(false);
  const nudgedRef = useRef(false);

  // Tick playSeconds while the kid is on a games route.
  useEffect(() => {
    if (!onGames) return;
    const id = setInterval(() => {
      incrementPlay(TICK_SECS);
      // Flush playtime to the backend so the admin sees per-visitor totals.
      trackEvent("playtime", { value: TICK_SECS });
    }, TICK_SECS * 1000);
    return () => clearInterval(id);
  }, [onGames, incrementPlay]);

  // Decide when to nudge / cap.
  useEffect(() => {
    if (playSeconds >= HARD_CAP - TICK_SECS && !playCapped) {
      // Treat "just about to cap" as capped so the soft-nudge with snooze
      // never appears in the last tick before the hard cap engages.
      setPlayCapped(true);
      setNudge(true);
      sfx.lose();
      return;
    }
    // soft nudge: reached soft break AND not currently snoozing.
    if (
      !nudgedRef.current &&
      !nudge &&
      !zen &&
      !yoga &&
      !playCapped &&
      playSeconds >= SOFT_BREAK_AT &&
      playSeconds >= (playSnoozeAt || 0)
    ) {
      nudgedRef.current = true;
      setNudge(true);
      sfx.pop();
    }
  }, [playSeconds, playSnoozeAt, playCapped, nudge, zen, yoga, setPlayCapped]);

  // If they left games, reset the "nudged" flag so the next session gets a fresh soft nudge.
  useEffect(() => {
    if (!onGames) nudgedRef.current = false;
  }, [onGames]);

  const closeAndReset = () => {
    setNudge(false);
    setZen(false);
    setYoga(false);
    resetPlaySession();
    setPlayCapped(false);
  };

  const onZenDone = () => {
    addCoins(ZEN_REWARD, { break: "zen" });
    pushNotification({
      kind: "reward",
      emoji: "🧘",
      title: `Zen break done! +${ZEN_REWARD} coins`,
      body: "Nicely relaxed. Back to play?",
    });
    closeAndReset();
  };
  const onYogaDone = () => {
    addCoins(YOGA_REWARD, { break: "yoga" });
    pushNotification({
      kind: "reward",
      emoji: "🤸",
      title: `Yoga stretch done! +${YOGA_REWARD} coins`,
      body: "Ahh, stretchy! Ready to play again?",
    });
    closeAndReset();
  };
  const onShop = () => {
    sfx.click();
    // Shopping counts as a break — dismiss the modal and reset play counter.
    closeAndReset();
    nav("/shop");
  };
  const onSnooze = () => {
    if (playCapped) return; // hard cap disables snooze
    sfx.click();
    setPlaySnooze(playSeconds + SNOOZE_SECONDS);
    setNudge(false);
    // Allow another nudge later once snoozeAt is reached.
    nudgedRef.current = false;
  };

  const modalOpen = nudge && !zen && !yoga;

  return (
    <>
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          style={{ background: "rgba(0,0,0,0.6)" }}
          data-testid="playtime-modal"
        >
          <div
            className="chunky-card w-full max-w-md p-5 sm:p-6 pop-in"
            style={{ background: "#fff" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="rounded-full flex items-center justify-center"
                style={{
                  width: 68,
                  height: 68,
                  background: playCapped ? "#FF5A5F" : "#FFB400",
                  border: "4px solid #2b2b2b",
                  boxShadow: "0 5px 0 #2b2b2b",
                }}
                aria-hidden="true"
              >
                <span className="text-4xl bob">{playCapped ? "🛑" : "⏰"}</span>
              </div>
              <div className="min-w-0">
                <div
                  className="inline-flex items-center gap-1 text-xs font-display px-2 py-0.5 rounded-full"
                  style={{
                    background: playCapped ? "#FF5A5F" : "#F368E0",
                    color: "#fff",
                    border: "2px solid #2b2b2b",
                  }}
                  data-testid="playtime-label"
                >
                  {playCapped ? (
                    <>
                      <ShieldAlert size={12} strokeWidth={3} /> Time out
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} strokeWidth={3} /> Break time
                    </>
                  )}
                </div>
                <h3 className="font-display text-2xl leading-tight mt-1">
                  {playCapped ? "Whoa, that's a lot of gaming!" : "You've been playing hard!"}
                </h3>
                <p className="text-sm mt-1" style={{ color: "#484848" }}>
                  {playCapped
                    ? `${fmtMin(playSeconds)} minutes today. Pick a break to unlock games again — no snoozing this time.`
                    : `${fmtMin(playSeconds)} minutes so far. Take a mini-break to feel fresh?`}
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => { sfx.click(); setZen(true); setNudge(false); }}
                className="btn-3d whitespace-nowrap"
                style={{ background: "#54A0FF" }}
                data-testid="break-zen-btn"
              >
                <Wind size={18} strokeWidth={3} />
                Zen · +{ZEN_REWARD}
              </button>
              <button
                onClick={() => { sfx.click(); setYoga(true); setNudge(false); }}
                className="btn-3d whitespace-nowrap"
                style={{ background: "#F368E0" }}
                data-testid="break-yoga-btn"
              >
                <Sparkles size={18} strokeWidth={3} />
                Yoga · +{YOGA_REWARD}
              </button>
              <button
                onClick={onShop}
                className="btn-3d whitespace-nowrap"
                style={{ background: "#FFB400" }}
                data-testid="break-shop-btn"
              >
                <ShoppingBag size={18} strokeWidth={3} />
                Shop
              </button>
              {!playCapped && (
                <button
                  onClick={onSnooze}
                  className="btn-3d btn-ghost whitespace-nowrap"
                  data-testid="break-snooze-btn"
                >
                  <Play size={18} strokeWidth={3} />
                  +5 min
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hard-cap game lock: if capped and modal was somehow dismissed, show a persistent lock overlay while on /games */}
      {playCapped && !modalOpen && !zen && !yoga && onGames && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4 py-6"
          style={{ background: "rgba(0,0,0,0.75)" }}
          data-testid="playtime-lock"
        >
          <div className="chunky-card p-6 max-w-md text-center" style={{ background: "#fff" }}>
            <div className="text-5xl mb-2">🛑</div>
            <h3 className="font-display text-2xl">Games are locked</h3>
            <p className="text-sm mt-2" style={{ color: "#484848" }}>
              Finish a Zen or Yoga break — or head to the shop — to unlock games again today.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button onClick={() => setNudge(true)} className="btn-3d" data-testid="playtime-lock-open">
                Choose a break
              </button>
            </div>
          </div>
        </div>
      )}

      <BreathBreak
        open={zen}
        onComplete={onZenDone}
        onSkip={() => {
          setZen(false);
          if (playCapped) setNudge(true);
        }}
      />
      <YogaBreak
        open={yoga}
        onComplete={onYogaDone}
        onSkip={() => {
          setYoga(false);
          if (playCapped) setNudge(true);
        }}
      />
    </>
  );
};

export default PlaytimeTracker;
